/** QR codes made on the device (uqr — small, no network): the module grid for a link, border added by the caller. */
import { encode } from 'uqr';

export function qrMatrix(text: string): boolean[][] {
  return encode(text, { ecc: 'M', border: 0 }).data;
}

/* ------------------------------------------------------------------------------------------------
 * 1.7.0 — the QR for a flyer: an SVG, and a PNG with a transparent background. Both are written here,
 * byte by byte, from the module grid: no canvas, no library that calls out, nothing leaves the device.
 * ---------------------------------------------------------------------------------------------- */

/** The quiet zone a scanner needs around the code, in modules. */
export const QR_QUIET = 4;

/** The grid for print: a stronger error correction than the on-screen codes, so a creased flyer still scans. */
export function qrForPrint(text: string): boolean[][] {
  return encode(text, { ecc: 'Q', border: 0 }).data;
}

const xml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** An SVG with no background (transparent): black modules, the quiet zone, and the title under it when given. */
export function qrSvg(matrix: boolean[][], o: { title?: string; scale?: number } = {}): string {
  const n = matrix.length;
  const scale = o.scale ?? 10;
  const side = (n + QR_QUIET * 2) * scale;
  const title = (o.title ?? '').trim();
  const textHeight = title ? Math.round(scale * 5) : 0;
  let d = '';
  for (let y = 0; y < n; y++) {
    let x = 0;
    while (x < n) {
      if (!matrix[y]![x]) {
        x++;
        continue;
      }
      let w = 1;
      while (x + w < n && matrix[y]![x + w]) w++;
      d += `M${(x + QR_QUIET) * scale} ${(y + QR_QUIET) * scale}h${w * scale}v${scale}h-${w * scale}z`;
      x += w;
    }
  }
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${side}" height="${side + textHeight}" viewBox="0 0 ${side} ${side + textHeight}" role="img" aria-label="${xml(title || 'QR code')}">`,
    `<path d="${d}" fill="#000"/>`,
    title ? `<text x="${side / 2}" y="${side + Math.round(scale * 1.5)}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="${Math.round(scale * 2.4)}" fill="#000">${xml(title)}</text>` : '',
    '</svg>',
  ].join('');
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(buf: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function adler32(buf: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < buf.length; i++) {
    a = (a + buf[i]!) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

const be32 = (n: number) => new Uint8Array([(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255]);

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const head = new Uint8Array([...type].map((c) => c.charCodeAt(0)));
  const body = concat([head, data]);
  return concat([be32(data.length), body, be32(crc32(body))]);
}

/** zlib "stored" blocks (no compression — one bit a pixel is small already). */
function zlibStored(raw: Uint8Array): Uint8Array {
  const parts: Uint8Array[] = [new Uint8Array([0x78, 0x01])];
  for (let at = 0; at < raw.length || at === 0; at += 65535) {
    const len = Math.min(65535, raw.length - at);
    const last = at + len >= raw.length ? 1 : 0;
    parts.push(new Uint8Array([last, len & 255, len >>> 8, ~len & 255, (~len >>> 8) & 255]), raw.subarray(at, at + len));
    if (!raw.length) break;
  }
  parts.push(be32(adler32(raw)));
  return concat(parts);
}

/**
 * A PNG with a transparent background: one bit a pixel, a two-colour palette (0 = fully transparent, 1 = black).
 * `scale` is pixels per module (16 prints sharp at flyer size); the quiet zone is transparent too, so the code
 * belongs on a light part of the flyer.
 */
export function qrPng(matrix: boolean[][], scale = 16): Uint8Array {
  const n = matrix.length;
  const side = (n + QR_QUIET * 2) * scale;
  const stride = Math.ceil(side / 8);
  const raw = new Uint8Array((stride + 1) * side);
  for (let y = 0; y < n; y++) {
    const line = new Uint8Array(stride);
    for (let x = 0; x < n; x++) {
      if (!matrix[y]![x]) continue;
      const from = (x + QR_QUIET) * scale;
      for (let px = from; px < from + scale; px++) line[px >> 3] = line[px >> 3]! | (0x80 >> (px & 7));
    }
    for (let r = 0; r < scale; r++) raw.set(line, ((y + QR_QUIET) * scale + r) * (stride + 1) + 1);
  }
  const ihdr = concat([be32(side), be32(side), new Uint8Array([1, 3, 0, 0, 0])]);
  return concat([
    new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
    chunk('IHDR', ihdr),
    chunk('PLTE', new Uint8Array([255, 255, 255, 0, 0, 0])),
    chunk('tRNS', new Uint8Array([0, 255])),
    chunk('IDAT', zlibStored(raw)),
    chunk('IEND', new Uint8Array(0)),
  ]);
}
